/* SellerCatalogBroadcast — email the (daily-updated) catalog to connected
 * buyers + prospect emails, on demand or on a daily/weekly schedule. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  Mail, Send, Users, UserPlus, Calendar, Loader2, ArrowLeft, ExternalLink,
  Package, CheckCircle2, AlertTriangle, Clock,
} from "lucide-react";

const SCHEDULES = [
  { value: "off",    label: "Off",    hint: "Manual sends only" },
  { value: "daily",  label: "Daily",  hint: "Every morning" },
  { value: "weekly", label: "Weekly", hint: "Once a week" },
];

export default function SellerCatalogBroadcast() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [schedule, setSchedule] = useState("off");
  const [sendToBuyers, setSendToBuyers] = useState(true);
  const [prospectsText, setProspectsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/seller/catalog-broadcast").then(r => {
      setData(r.data);
      const s = r.data.settings || {};
      setSchedule(s.schedule || "off");
      setSendToBuyers(s.send_to_buyers !== false);
      setProspectsText((s.prospect_emails || []).join("\n"));
    }).catch(e => toast.error(formatErr(e.response?.data?.detail) || "Could not load"));
  };
  useEffect(() => { load(); }, []);

  const parseProspects = () =>
    prospectsText.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = {
        schedule,
        send_to_buyers: sendToBuyers,
        prospect_emails: parseProspects(),
        ...overrides,
      };
      const { data: res } = await api.put("/seller/catalog-broadcast", payload);
      setData(d => ({ ...d, settings: res.settings, recipient_counts: res.recipient_counts }));
      toast.success("Saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      // Persist current edits first so the send uses them.
      await save();
      const { data: res } = await api.post("/seller/catalog-broadcast/send");
      if (res.sent > 0) {
        toast.success(`Sent to ${res.sent} recipient${res.sent !== 1 ? "s" : ""}${res.failed ? ` · ${res.failed} failed` : ""}`);
      } else {
        toast.warning(`No emails went out${res.failed ? ` · ${res.failed} failed (email provider limit)` : ""}`);
      }
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  if (!data) {
    return <div className="grid place-items-center py-24"><Loader2 className="w-6 h-6 animate-spin text-foreground/40"/></div>;
  }

  const counts = data.recipient_counts || { buyers: 0, prospects: 0, total: 0 };
  const lastSent = data.settings?.last_sent_at;

  return (
    <div data-testid="seller-catalog-broadcast">
      <PageHeader
        title="Email catalog"
        subtitle="Send your latest products to buyers & prospects — on demand or on a schedule"
        action={
          <Button variant="outline" className="rounded-full" onClick={() => nav("/seller/catalog")} data-testid="broadcast-back">
            <ArrowLeft className="w-4 h-4 mr-1.5"/>Back to catalog
          </Button>
        }
      />

      <div className="page-shell grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-6">
          {/* Recipients */}
          <Section title="Who receives it" icon={Users}>
            <div className="flex items-center justify-between gap-4 py-2">
              <div>
                <div className="font-medium text-sm">Connected buyers</div>
                <div className="text-[12.5px] text-foreground/55">Everyone with an active connection to your store</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm mono text-foreground/70" data-testid="broadcast-buyer-count">{counts.buyers}</span>
                <Switch checked={sendToBuyers} onCheckedChange={setSendToBuyers} data-testid="broadcast-send-buyers"/>
              </div>
            </div>
            <div className="border-t border-border pt-4 mt-2">
              <div className="flex items-center gap-2 mb-2">
                <UserPlus className="w-4 h-4 text-foreground/55"/>
                <div className="font-medium text-sm">Prospect emails</div>
                <span className="text-[11px] text-foreground/45">· potential buyers (one per line)</span>
              </div>
              <Textarea
                value={prospectsText}
                onChange={e => setProspectsText(e.target.value)}
                placeholder={"buyer1@store.com\nbuyer2@boutique.com"}
                rows={5}
                className="text-sm font-mono"
                data-testid="broadcast-prospects"
              />
              <div className="text-[11.5px] text-foreground/50 mt-1.5">
                {parseProspects().length} prospect{parseProspects().length !== 1 ? "s" : ""} · prospects can browse + sign up to order from your storefront link.
              </div>
            </div>
          </Section>

          {/* Schedule */}
          <Section title="Schedule" icon={Calendar}>
            <div className="grid sm:grid-cols-3 gap-3">
              {SCHEDULES.map(s => (
                <button
                  key={s.value}
                  onClick={() => setSchedule(s.value)}
                  data-testid={`broadcast-schedule-${s.value}`}
                  className={`rounded-xl border p-4 text-left transition-colors ${
                    schedule === s.value
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-white hover:border-foreground/40"}`}
                >
                  <div className="font-medium text-sm">{s.label}</div>
                  <div className={`text-[12px] mt-0.5 ${schedule === s.value ? "text-background/70" : "text-foreground/55"}`}>{s.hint}</div>
                </button>
              ))}
            </div>
            <div className="text-[12px] text-foreground/55 mt-3">
              Scheduled sends go out automatically with your latest catalog. You can also send any time below.
            </div>
          </Section>

          {/* Recent sends */}
          <Section title="Recent sends" icon={Clock}>
            {(data.log || []).length === 0 ? (
              <div className="text-sm text-foreground/50 py-2">No catalog emails sent yet.</div>
            ) : (
              <div className="divide-y divide-border" data-testid="broadcast-log">
                {data.log.map(l => (
                  <div key={l.id} className="flex items-center justify-between py-2.5 text-sm">
                    <div className="flex items-center gap-2">
                      {l.sent_count > 0
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/>
                        : <AlertTriangle className="w-4 h-4 text-amber-600"/>}
                      <span className="text-foreground/75">
                        {new Date(l.sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                      <span className="text-[11px] text-foreground/45 mono uppercase tracking-wide">{l.trigger}</span>
                    </div>
                    <div className="text-[12px] mono text-foreground/60">
                      {l.sent_count}/{l.recipient_count} sent{l.failed_count ? ` · ${l.failed_count} failed` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-white p-5 sticky top-24">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Ready to send</div>
            <div className="display text-4xl mt-3" data-testid="broadcast-total-recipients">{counts.total}</div>
            <div className="text-[12.5px] text-foreground/55 mt-1">recipients ({counts.buyers} buyers · {counts.prospects} prospects)</div>

            <div className="flex items-center gap-2 mt-4 text-[12.5px] text-foreground/70">
              <Package className="w-3.5 h-3.5"/>{data.product_count} products in this email
            </div>
            {lastSent && (
              <div className="text-[11.5px] text-foreground/45 mt-1">Last sent {new Date(lastSent).toLocaleString()}</div>
            )}

            <Button
              onClick={sendNow}
              disabled={sending || counts.total === 0 || data.product_count === 0}
              data-testid="broadcast-send-now"
              className="w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90 mt-5"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
              Send now
            </Button>
            <Button
              onClick={() => save()}
              disabled={saving}
              variant="outline"
              data-testid="broadcast-save"
              className="w-full h-10 rounded-full mt-2"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
              Save settings
            </Button>

            {!data.email_configured && (
              <div className="mt-4 text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
                Email delivery isn't configured yet — contact support to go live.
              </div>
            )}

            {data.storefront_url && (
              <a href={data.storefront_url} target="_blank" rel="noopener noreferrer"
                 data-testid="broadcast-storefront-link"
                 className="mt-4 flex items-center gap-1.5 text-[12px] text-foreground/55 hover:text-foreground">
                <ExternalLink className="w-3.5 h-3.5"/>Preview storefront link
              </a>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-foreground/60"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">{title}</div>
      </div>
      {children}
    </div>
  );
}
